<template>
    <Teleport to="body">
        <Transition
            enter-active-class="transition ease-out duration-200"
            enter-from-class="opacity-0"
            leave-active-class="transition ease-in duration-150"
            leave-to-class="opacity-0"
        >
            <div v-if="show" class="fixed inset-0 z-40 bg-gray-900/50" @click="$emit('close')" />
        </Transition>

        <Transition
            enter-active-class="transition ease-out duration-200"
            enter-from-class="translate-y-8 opacity-0"
            leave-active-class="transition ease-in duration-150"
            leave-to-class="translate-y-8 opacity-0"
        >
            <div
                v-if="show && space"
                class="fixed inset-x-0 bottom-0 z-50 max-h-[90vh] overflow-y-auto bg-white rounded-t-2xl px-4 pt-2 pb-4"
                role="dialog"
                aria-modal="true"
                aria-labelledby="space-sheet-title"
            >
                <div class="w-9 h-1 mx-auto mb-3 rounded-full bg-gray-200" />

                <div class="flex items-start justify-between gap-3">
                    <div>
                        <h2 id="space-sheet-title" class="text-lg font-bold tracking-tight">{{ space.name }}</h2>
                        <p class="text-sm text-gray-600">Table details</p>
                    </div>
                    <button
                        type="button"
                        class="shrink-0 flex items-center justify-center w-11 h-11 rounded-full bg-gray-100 transition hover:bg-gray-200 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-700"
                        aria-label="Close"
                        @click="$emit('close')"
                    >
                        <i class="fa-solid fa-xmark text-gray-700" aria-hidden="true" />
                    </button>
                </div>

                <div class="flex gap-2 mt-3.5">
                    <div
                        class="grow h-14 flex flex-col items-center justify-center rounded-[0.625rem] border-[1.5px] border-gray-200 bg-white"
                    >
                        <span class="text-base font-bold text-gray-900">{{ space.size }}</span>
                        <span class="text-sm font-semibold text-gray-600">Table size</span>
                    </div>
                    <div
                        class="grow h-14 flex flex-col items-center justify-center rounded-[0.625rem] border-[1.5px] border-gray-200 bg-white"
                    >
                        <span class="text-base font-bold text-gray-900">{{ space.seats }}</span>
                        <span class="text-sm font-semibold text-gray-600">Max seats</span>
                    </div>
                </div>

                <p v-if="minimumLabel" class="mt-2.5 text-center text-sm text-gray-600">{{ minimumLabel }}</p>

                <template v-if="space.description">
                    <p class="mt-3.5 text-sm font-bold uppercase tracking-wider text-gray-600">About this table</p>
                    <div class="sheet-copy mt-2 text-base leading-relaxed text-gray-700" v-html="space.description" />
                </template>

                <button
                    type="button"
                    class="w-full h-14 mt-3.5 flex items-center justify-center rounded-[0.625rem] bg-primary text-sm font-bold uppercase tracking-widest text-white transition hover:bg-tertiary hover:text-black focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-700"
                    @click="$emit('close')"
                >
                    Close
                </button>
            </div>
        </Transition>
    </Teleport>
</template>

<script setup>
    import { computed, onBeforeUnmount, onMounted } from "vue";
    import { minimumHours } from "../../Composables/useBookingSchedule.js";

    const props = defineProps({
        show: Boolean,
        space: Object,
    });

    const emits = defineEmits(["close"]);

    // The booking sheet quotes this too, so say it before someone picks a slot it rules out.
    const minimumLabel = computed(() => {
        const minimum = minimumHours(props.space);

        return minimum > 1 ? `Booked in blocks of ${minimum} hours or more` : "";
    });

    // A sheet with aria-modal has to answer Escape, or keyboard users are stuck in it.
    const closeOnEscape = (event) => {
        if (event.key === "Escape" && props.show) {
            emits("close");
        }
    };

    onMounted(() => window.addEventListener("keydown", closeOnEscape));

    onBeforeUnmount(() => window.removeEventListener("keydown", closeOnEscape));
</script>

<style scoped>
    /* The description is stored as HTML, so it arrives without scope attributes. */
    .sheet-copy :deep(p) {
        margin-bottom: 0.75rem;
    }

    .sheet-copy :deep(p:last-child) {
        margin-bottom: 0;
    }

    .sheet-copy :deep(ul) {
        list-style: disc;
        padding-left: 1.25rem;
    }

    .sheet-copy :deep(strong) {
        font-weight: 700;
    }
</style>
